import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Link as LinkIcon, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useTransparenceStore,
  TransparenceLink
} from "@/data/transparenceStore";
import { uploadFile } from "@/utils/uploadHelpers";

const AdminTransparencePage = () => {
  const { links, addLink, updateLink, deleteLink, fetchLinks } = useTransparenceStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState<Partial<TransparenceLink> | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const handleSave = () => {
    if (!currentLink?.title?.fr || !currentLink?.url) {
      toast.error("Veuillez remplir le titre (FR) et l'URL au minimum.");
      return;
    }

    if (currentLink.id) {
      updateLink(currentLink.id, currentLink);
      toast.success("Lien mis à jour avec succès.");
    } else {
      addLink({
        title: currentLink.title || { fr: "", ar: "", zgh: "" },
        desc: currentLink.desc || { fr: "", ar: "", zgh: "" },
        url: currentLink.url,
        iconName: currentLink.iconName || "ExternalLink",
        imageUrl: currentLink.imageUrl || "",
        internal: currentLink.internal || false,
      });
      toast.success("Lien ajouté avec succès.");
    }
    setIsDialogOpen(false);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce lien ?")) {
      deleteLink(id);
      toast.success("Lien supprimé.");
    }
  };

  const openDialog = (link?: TransparenceLink) => {
    if (link) {
      setCurrentLink(link);
    } else {
      setCurrentLink({
        title: { fr: "", ar: "", zgh: "" },
        desc: { fr: "", ar: "", zgh: "" },
        url: "",
        iconName: "ExternalLink",
        imageUrl: "",
        internal: false
      });
    }
    setIsDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB.");
      return;
    }

    try {
      setIsUploading(true);
      toast.info("Upload en cours...");
      const url = await uploadFile(file);
      setCurrentLink(prev => ({ ...prev, imageUrl: url }));
      toast.success("Image chargée.");
    } catch (error) {
      console.error("Erreur d'upload:", error);
      toast.error("Une erreur est survenue lors du chargement de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleTitleChange = (lang: 'fr' | 'ar' | 'zgh', val: string) => {
    setCurrentLink(prev => ({
      ...prev, title: { ...prev?.title, [lang]: val } as any
    }));
  };

  const handleDescChange = (lang: 'fr' | 'ar' | 'zgh', val: string) => {
    setCurrentLink(prev => ({
      ...prev, desc: { ...prev?.desc, [lang]: val } as any
    }));
  };

  const filteredLinks = links.filter(l => 
    l.title.fr.toLowerCase().includes(searchTerm.toLowerCase()) || 
    l.title.ar.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-card p-6 rounded-2xl border border-border shadow-sm">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Paramètres de Transparence</h1>
          <p className="text-muted-foreground font-body mt-1">Gérez les plateformes et actions citoyennes affichées sur la page d'accueil.</p>
        </div>
        <Button onClick={() => openDialog()} className="gap-2">
          <Plus className="w-4 h-4" /> Ajouter une plateforme
        </Button>
      </div>

      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Search className="w-5 h-5 text-muted-foreground" />
          <Input 
            placeholder="Rechercher une plateforme..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border-none shadow-none focus-visible:ring-0 max-w-sm"
          />
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plateforme / Logo</TableHead>
              <TableHead>Titre (FR / AR)</TableHead>
              <TableHead>URL</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLinks.map((link) => (
              <TableRow key={link.id}>
                <TableCell>
                  {link.imageUrl ? (
                    <img src={link.imageUrl} alt={link.title.fr} className="w-8 h-8 rounded object-contain border" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs font-bold font-mono">
                      {link.iconName}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <p className="font-semibold">{link.title.fr}</p>
                  <p className="text-sm text-muted-foreground">{link.title.ar}</p>
                </TableCell>
                <TableCell>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                    <LinkIcon className="w-3 h-3" /> {link.url.replace(/^https?:\/\//, '').substring(0, 30)}...
                  </a>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openDialog(link)}>
                    <Edit2 className="w-4 h-4 text-primary" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(link.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentLink?.id ? "Modifier la plateforme" : "Ajouter une plateforme"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de l'icône de secours</Label>
                <Input value={currentLink?.iconName || ""} onChange={e => setCurrentLink({...currentLink, iconName: e.target.value})} placeholder="ex: FileText, Users, Building2..." />
              </div>
              <div className="space-y-2">
                <Label>Logo / Image (Optionnel)</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/*" onChange={handleImageUpload} className="w-1/2 cursor-pointer" />
                  <Input value={currentLink?.imageUrl || ""} onChange={e => setCurrentLink({...currentLink, imageUrl: e.target.value})} placeholder="URL de l'image..." className="w-1/2" />
                </div>
                {isUploading && <p className="text-xs text-primary font-bold">Upload en cours...</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Lien de destination (URL)</Label>
              <Input value={currentLink?.url || ""} onChange={e => setCurrentLink({...currentLink, url: e.target.value})} placeholder="https://..." />
            </div>

            <div className="border border-border rounded-xl p-4 bg-muted/30 space-y-4">
              <h4 className="font-semibold text-sm">Français</h4>
              <div className="space-y-2">
                <Label>Titre (FR)</Label>
                <Input value={currentLink?.title?.fr || ""} onChange={e => handleTitleChange('fr', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Description (FR)</Label>
                <Input value={currentLink?.desc?.fr || ""} onChange={e => handleDescChange('fr', e.target.value)} />
              </div>
            </div>

            <div className="border border-border rounded-xl p-4 bg-muted/30 space-y-4">
              <h4 className="font-semibold text-sm">Arabe</h4>
              <div className="space-y-2">
                <Label className="text-right block">العنوان (عربي)</Label>
                <Input dir="rtl" value={currentLink?.title?.ar || ""} onChange={e => handleTitleChange('ar', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-right block">الوصف (عربي)</Label>
                <Input dir="rtl" value={currentLink?.desc?.ar || ""} onChange={e => handleDescChange('ar', e.target.value)} />
              </div>
            </div>
            
            <div className="border border-border rounded-xl p-4 bg-muted/30 space-y-4">
              <h4 className="font-semibold text-sm">Amazigh (Tifinagh)</h4>
              <div className="space-y-2">
                <Label>Titre (ZGH)</Label>
                <Input value={currentLink?.title?.zgh || ""} onChange={e => handleTitleChange('zgh', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Description (ZGH)</Label>
                <Input value={currentLink?.desc?.zgh || ""} onChange={e => handleDescChange('zgh', e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isUploading}>Annuler</Button>
            <Button onClick={handleSave} disabled={isUploading}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTransparencePage;
